import IcDelete from '@assets/details-camera-icons/IcDelete';
import IcEditGeoZone from '@assets/details-camera-icons/IcEditGeoZone';
import BaseCheckbox from '@components/BaseCheckbox';
import BaseIcon from '@components/BaseIcon';
import IOSSwitch from '@components/IOSSwitch';
import { BaseHead, CellOfRow } from '@components/tables';
import { pushSuccess } from '@components/toast';
import { geoZoneController, locationController } from '@controllers/index';
import {
    ContraventionReasonTranslations,
    GeoZones,
    GeoZoneType,
    Status,
    Location,
    CameraWithInfo,
} from '@EcamModel/model';
import { Zones } from '@EcamModel/model/Zones';
import {
    Autocomplete,
    Box,
    FormControlLabel,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import ListNoData from '@pages/cameras/list/components/NoDataCameras';
import { autocompleteStyles } from '@pages/overview/smartFilterHighLight/components/BaseAutoCompleteForTypeMapping';
import { convertSecondsToDate } from '@utils/functions';
import { formatSecondsToHHMMSS } from '../../../utils';
import { groupBy } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { formatUKDate } from 'src/helpers';
import { BsQuestion } from 'react-icons/bs';
import { useBackdrop } from 'src/providers/BackdropProvider';
import { DetectMode, detectModes } from '../../EditGeoZones';
import GeoZoneActionButtons from './GeoZoneActionButtons';
import { detectModesMapping } from '../../cameraRecordedEventLog/configs';
import { BaseTextField } from '@components/BaseTextField';
import { useQueryClient } from 'react-query';

type EditingGeoZoneData = Partial<GeoZones> & {
    Location?: Location;
    Zone?: Zones;
};

interface BaseTableProps {
    rows: GeoZones[];
    handleDelete: (item: GeoZones) => void;
    onUpdate?: (updatedGeoZone: GeoZones) => void;
    camera?: CameraWithInfo;
}

const BaseTableGeoZonesDetailPTZ: React.FC<BaseTableProps> = (props) => {
    const { rows, handleDelete, onUpdate, camera } = props;
    const theme = useTheme();
    const backdrop = useBackdrop();
    const queryClient = useQueryClient();

    const [contraventionReasons, setContraventionReasons] = useState<ContraventionReasonTranslations[]>([]);
    const [editingGeoZoneId, setEditingGeoZoneId] = useState<number | null>(null);
    const [editingData, setEditingData] = useState<EditingGeoZoneData>({});
    const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
    const [zones, setZones] = useState<Zones[]>([]);

    useEffect(() => {
        geoZoneController.getContraventionReasonTranslations().then((res) => {
            setContraventionReasons(res);
        });
    }, []);

    useEffect(() => {
        if (!camera) return;

        const locationMap = new Map<number, Location>();

        if (camera.Zone?.Location) {
            const primaryLoc = camera.Zone.Location;
            locationMap.set(primaryLoc.Id!, {
                ...primaryLoc,
                Zones: [camera.Zone],
            });
        }

        if (camera.ExtendedCoverage && Array.isArray(camera.ExtendedCoverage)) {
            (camera.ExtendedCoverage as Location[]).forEach((extLoc) => {
                const existingLoc = locationMap.get(extLoc.Id!);

                if (existingLoc) {
                    const mergedZones = [...(existingLoc.Zones || [])];
                    (extLoc.Zones || []).forEach((zone) => {
                        if (!mergedZones.some((z) => z.Id === zone.Id)) {
                            mergedZones.push(zone);
                        }
                    });
                    locationMap.set(extLoc.Id!, {
                        ...extLoc,
                        Zones: mergedZones,
                    });
                } else {
                    locationMap.set(extLoc.Id!, extLoc);
                }
            });
        }

        setAvailableLocations(Array.from(locationMap.values()));
    }, [camera]);

    const handleEditGeoZone = (geoZone: GeoZones) => {
        setEditingGeoZoneId(geoZone.Id!);

        const zoneToUse = geoZone.Zone || camera?.Zone;
        let selectedLocation: Location | undefined;
        let selectedZone: Zones | undefined;

        if (zoneToUse?.Location && availableLocations.length > 0) {
            selectedLocation = availableLocations.find((loc) => loc.Id === zoneToUse.Location?.Id);
            if (selectedLocation) {
                setZones(selectedLocation.Zones || []);
                selectedZone = selectedLocation.Zones?.find((z) => z.Id === zoneToUse.Id);
            }
        }

        setEditingData({
            GeoZoneName: geoZone.GeoZoneName,
            ContraventionReasonId: geoZone.ContraventionReasonId,
            ValidationType: geoZone.ValidationType,
            Status: geoZone.Status,
            PermitExemptionsEnabled: geoZone.PermitExemptionsEnabled,
            Location: selectedLocation,
            Zone: selectedZone || zoneToUse,
            ZoneId: geoZone.ZoneId || zoneToUse?.Id,
        });
    };

    const handleSaveGeoZone = async (geoZone: GeoZones) => {
        backdrop.setTrue();
        try {
            const updatedGeoZone: EditingGeoZoneData = {
                ...geoZone,
                ...editingData,
                ZoneId: editingData.ZoneId || editingData.Zone?.Id,
            };

            const zoneDataWithLocation = editingData.Zone
                ? {
                      ...editingData.Zone,
                      Location: editingData.Location,
                  }
                : undefined;

            if (updatedGeoZone.ContraventionReason) delete updatedGeoZone.ContraventionReason;
            if (updatedGeoZone.Location) delete updatedGeoZone.Location;
            if (updatedGeoZone.Zone) delete updatedGeoZone.Zone;

            await geoZoneController.upsert(updatedGeoZone as GeoZones).then((res) => {
                onUpdate?.({
                    ...(updatedGeoZone as GeoZones),
                    ContraventionReason: res.ContraventionReason,
                    Zone: zoneDataWithLocation,
                });
            });
            queryClient.invalidateQueries(['trailAuditEvent']);
            pushSuccess('Updated successfully');
            setEditingGeoZoneId(null);
            setEditingData({});
        } catch (error) {
            console.error('Error updating geo zone:', error);
        } finally {
            backdrop.setFalse();
        }
    };

    const handleCancelEdit = () => {
        setEditingGeoZoneId(null);
        setEditingData({});
    };

    const handleEditDataChange = (field: keyof EditingGeoZoneData, value: any) => {
        setEditingData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleKeyPress = (event: React.KeyboardEvent, geoZone: GeoZones) => {
        if (event.key === 'Enter' && editingGeoZoneId === geoZone.Id) {
            handleSaveGeoZone(geoZone);
        }
    };

    const handleLocationChange = (location: Location | null) => {
        if (location) {
            const locationZones = location.Zones || [];
            setZones(locationZones);
            handleEditDataChange('Location', location);

            if (locationZones.length > 0) {
                const firstZone = locationZones[0];
                handleEditDataChange('Zone', firstZone);
                handleEditDataChange('ZoneId', firstZone.Id);
            } else {
                handleEditDataChange('Zone', undefined);
                handleEditDataChange('ZoneId', undefined);
            }
        } else {
            setZones([]);
            handleEditDataChange('Location', undefined);
            handleEditDataChange('Zone', undefined);
            handleEditDataChange('ZoneId', undefined);
        }
    };
    return (
        <TableContainer component={Box} mt={2}>
            {!rows?.filter((gz) => gz.GeoZoneType === GeoZoneType.Detail).length ? (
                <ListNoData />
            ) : (
                <Table
                    aria-label="simple table"
                    sx={{
                        '& .MuiTableCell-root': {
                            padding: '8px',
                            whiteSpace: 'nowrap',
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <BaseHead align="left" title="Current operation hours" />
                            <BaseHead align="center" title="Geo-zone name" />
                            <BaseHead align="left" title="Location" />
                            <BaseHead align="left" title="Zone" />
                            <BaseHead align="center" title="Contravention type" />
                            <BaseHead align="center" title="Detect mode" />
                            <BaseHead align="center" title="Status" />
                            <BaseHead align="center" title="Exempt VRNs" />
                            <BaseHead align="center" title="" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((item, index) => {
                            if (item.GeoZoneType === GeoZoneType.Overview) return <></>;

                            const isEditing = editingGeoZoneId === item.Id;
                            const operationHoursConfigs = item.GZOConfigs?.[0];
                            const operationHours = operationHoursConfigs?.GZOConfigGracePeriods.flatMap((item) =>
                                item.GZOConfigDays.map((item) => item)
                            );

                            const _operationHours = groupBy(operationHours, 'Weekday');

                            return (
                                <TableRow
                                    key={`${index}+1`}
                                    sx={{
                                        verticalAlign: !!operationHoursConfigs?.EffectiveFrom ? 'top' : 'middle',
                                    }}
                                >
                                    <CellOfRow
                                        value={
                                            <Stack>
                                                {!!operationHoursConfigs?.EffectiveFrom ? (
                                                    <Typography color="#256428">
                                                        Effective from:{' '}
                                                        {formatUKDate(operationHoursConfigs?.EffectiveFrom)}
                                                    </Typography>
                                                ) : (
                                                    <Typography fontStyle="italic" color={theme.palette.grey[600]}>
                                                        No current operation hours
                                                    </Typography>
                                                )}
                                                {Object.entries(_operationHours).map(([key, value], index) => (
                                                    <Stack
                                                        direction="row"
                                                        alignItems="flex-start"
                                                        spacing={1}
                                                        mb={1}
                                                        key={key}
                                                    >
                                                        <Typography minWidth={80}>
                                                            {moment().isoWeekday(parseInt(key)).format('dddd')}
                                                        </Typography>
                                                        <Stack>
                                                            {value.map((o) => {
                                                                const geoZoneConfig =
                                                                    operationHoursConfigs?.GZOConfigGracePeriods.find(
                                                                        (p) =>
                                                                            p.Id ===
                                                                            o.GeoZoneOperationConfigGracePeriodId
                                                                    );
                                                                return (
                                                                    <Stack
                                                                        direction="row"
                                                                        alignItems="center"
                                                                        spacing={0.5}
                                                                        pr={2}
                                                                        key={o.GeoZoneOperationConfigGracePeriodId}
                                                                        whiteSpace={'nowrap'}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                p: 0.4,
                                                                                background: '#256428',
                                                                                borderRadius: 1,
                                                                            }}
                                                                        />
                                                                        <Typography color="GrayText">
                                                                            {moment(
                                                                                convertSecondsToDate(o.From!)
                                                                            ).format('HH:mm')}
                                                                            -
                                                                            {moment(convertSecondsToDate(o.To!)).format(
                                                                                'HH:mm'
                                                                            )}{' '}
                                                                            | Grace period:{' '}
                                                                            {geoZoneConfig?.GracePeriod != null ? formatSecondsToHHMMSS(geoZoneConfig?.GracePeriod) : '-'}
                                                                        </Typography>
                                                                    </Stack>
                                                                );
                                                            })}
                                                        </Stack>
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        }
                                        align="left"
                                    />

                                    <CellOfRow
                                        value={
                                            <Box sx={{ minWidth: 180, position: 'relative' }}>
                                                {isEditing ? (
                                                    <BaseTextField
                                                        autoFocus
                                                        value={editingData.GeoZoneName || ''}
                                                        onChange={(e) =>
                                                            handleEditDataChange('GeoZoneName', e.target.value)
                                                        }
                                                        onKeyPress={(e) => handleKeyPress(e, item)}
                                                        placeholder="Enter geo-zone name"
                                                    />
                                                ) : (
                                                    <Typography>{item.GeoZoneName}</Typography>
                                                )}
                                            </Box>
                                        }
                                        align="center"
                                    />

                                    <CellOfRow
                                        value={
                                            <Box sx={{ minWidth: 180, position: 'relative' }}>
                                                {isEditing ? (
                                                    <Autocomplete
                                                        options={availableLocations}
                                                        getOptionLabel={(o) => o.Name}
                                                        value={
                                                            availableLocations.find(
                                                                (loc) => loc.Id === editingData.Location?.Id
                                                            ) || null
                                                        }
                                                        isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
                                                        onChange={(_, val) => handleLocationChange(val)}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Location"
                                                                placeholder="Select location"
                                                                InputLabelProps={{ shrink: true }}
                                                                onKeyPress={(e) => handleKeyPress(e, item)}
                                                            />
                                                        )}
                                                        disableClearable={false}
                                                        clearIcon={<></>}
                                                        sx={{
                                                            ...autocompleteStyles,
                                                            '& .MuiAutocomplete-endAdornment': {
                                                                top: '50%',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography fontSize={'14px'}>
                                                        {item.Zone?.Location?.Name ||
                                                            camera?.Zone?.Location?.Name ||
                                                            ''}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        align="left"
                                    />

                                    <CellOfRow
                                        value={
                                            <Box sx={{ minWidth: 180, position: 'relative' }}>
                                                {isEditing ? (
                                                    <Autocomplete
                                                        options={zones}
                                                        getOptionLabel={(o) => o.PublicName}
                                                        value={zones.find((z) => z.Id === editingData.Zone?.Id) || null}
                                                        isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
                                                        onChange={(_, val) => {
                                                            if (val) {
                                                                handleEditDataChange('Zone', val);
                                                                handleEditDataChange('ZoneId', val.Id);
                                                            }
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Zone"
                                                                placeholder="Select zone"
                                                                InputLabelProps={{ shrink: true }}
                                                                onKeyPress={(e) => handleKeyPress(e, item)}
                                                            />
                                                        )}
                                                        disableClearable={false}
                                                        clearIcon={<></>}
                                                        disabled={!editingData.Location}
                                                        sx={{
                                                            ...autocompleteStyles,
                                                            '& .MuiAutocomplete-endAdornment': {
                                                                top: '50%',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography fontSize={'14px'}>
                                                        {item.Zone?.PublicName || camera?.Zone?.PublicName || ''}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        align="left"
                                    />

                                    <CellOfRow
                                        value={
                                            <Box sx={{ minWidth: 180, position: 'relative' }}>
                                                {isEditing ? (
                                                    <Autocomplete
                                                        options={contraventionReasons}
                                                        getOptionLabel={(o) => o.Summary}
                                                        value={
                                                            contraventionReasons.find(
                                                                (r) =>
                                                                    r.ContraventionReasonId ===
                                                                    editingData.ContraventionReasonId
                                                            ) || null
                                                        }
                                                        isOptionEqualToValue={(opt, val) =>
                                                            opt.ContraventionReasonId === val.ContraventionReasonId
                                                        }
                                                        onChange={(_, val) => {
                                                            if (val) {
                                                                handleEditDataChange(
                                                                    'ContraventionReasonId',
                                                                    val?.ContraventionReasonId
                                                                );
                                                            }
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Contravention reason"
                                                                placeholder="Select reason"
                                                                InputLabelProps={{ shrink: true }}
                                                                onKeyPress={(e) => handleKeyPress(e, item)}
                                                            />
                                                        )}
                                                        disableClearable={false}
                                                        clearIcon={<></>}
                                                        sx={{
                                                            ...autocompleteStyles,
                                                            '& .MuiAutocomplete-endAdornment': {
                                                                top: '50%',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography>
                                                        {
                                                            item.ContraventionReason
                                                                ?.ContraventionReasonTranslations?.[0]?.Summary!
                                                        }
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        align="center"
                                    />
                                    <CellOfRow
                                        value={
                                            <Box sx={{ minWidth: 100, position: 'relative' }}>
                                                {isEditing ? (
                                                    <Autocomplete
                                                        options={detectModes}
                                                        getOptionLabel={(o) => o.Label}
                                                        value={
                                                            detectModes.find(
                                                                (m) => m.Value === editingData.ValidationType
                                                            ) || null
                                                        }
                                                        isOptionEqualToValue={(opt, val) => opt.Value === val.Value}
                                                        onChange={(_, val) => {
                                                            if (val) handleEditDataChange('ValidationType', val?.Value);
                                                        }}
                                                        renderOption={(props, option) => (
                                                            <li {...props}>
                                                                <Stack
                                                                    direction="row"
                                                                    justifyContent={'space-between'}
                                                                    alignItems="center"
                                                                    spacing={1}
                                                                    flex={1}
                                                                >
                                                                    <Typography>{option.Label}</Typography>
                                                                    <Tooltip title={option.Note} arrow placement="top">
                                                                        <Stack
                                                                            alignItems="center"
                                                                            justifyContent="center"
                                                                            sx={{
                                                                                p: 0.1,
                                                                                background: (theme) =>
                                                                                    theme.palette.info.main,
                                                                                borderRadius: '100%',
                                                                            }}
                                                                        >
                                                                            <BsQuestion color="#fff" fontSize={16} />
                                                                        </Stack>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </li>
                                                        )}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Detect mode"
                                                                placeholder="Select mode"
                                                                InputLabelProps={{ shrink: true }}
                                                                onKeyPress={(e) => handleKeyPress(e, item)}
                                                            />
                                                        )}
                                                        clearIcon={<></>}
                                                        sx={{
                                                            ...autocompleteStyles,
                                                            '& .MuiAutocomplete-endAdornment': {
                                                                top: '50%',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography>{detectModesMapping[item.ValidationType!]}</Typography>
                                                )}
                                            </Box>
                                        }
                                        align="center"
                                    />
                                    <CellOfRow
                                        value={
                                            <Box sx={{ position: 'relative' }}>
                                                {isEditing ? (
                                                    <IOSSwitch
                                                        label={
                                                            <Typography>
                                                                {editingData.Status === Status.Active
                                                                    ? 'Active'
                                                                    : 'Inactive'}
                                                            </Typography>
                                                        }
                                                        iosSwitchProps={{
                                                            checked: isEditing
                                                                ? editingData.Status === Status.Active
                                                                : item.Status === Status.Active,
                                                            onChange(event, checked) {
                                                                handleEditDataChange(
                                                                    'Status',
                                                                    checked ? Status.Active : Status.Inactive
                                                                );
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        spacing={1}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 5,
                                                                height: 5,
                                                                background:
                                                                    item.Status === Status.Active
                                                                        ? '#2E7D32'
                                                                        : '#E01B00',
                                                                borderRadius: '100%',
                                                                animation: 'blinker 2s linear infinite',
                                                                '@keyframes blinker': {
                                                                    '0%': {
                                                                        opacity: 1,
                                                                    },
                                                                    '50%': {
                                                                        opacity: 0.5,
                                                                    },
                                                                    '100%': {
                                                                        opacity: 1,
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                        <Typography>
                                                            {item.Status === Status.Active ? 'Active' : 'Inactive'}
                                                        </Typography>
                                                    </Stack>
                                                )}
                                            </Box>
                                        }
                                        align="center"
                                    />
                                    <CellOfRow
                                        value={
                                            <Box sx={{ position: 'relative' }}>
                                                {isEditing ? (
                                                    <FormControlLabel
                                                        control={
                                                            <BaseCheckbox
                                                                checked={editingData.PermitExemptionsEnabled!}
                                                                onChange={(_, checked) => {
                                                                    handleEditDataChange(
                                                                        'PermitExemptionsEnabled',
                                                                        checked
                                                                    );
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Typography sx={{ ml: 1 }}>
                                                                {editingData.PermitExemptionsEnabled ? 'On' : 'Off'}
                                                            </Typography>
                                                        }
                                                    />
                                                ) : (
                                                    <Typography>
                                                        {item.PermitExemptionsEnabled ? 'On' : 'Off'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        align="center"
                                    />

                                    <CellOfRow
                                        value={
                                            <GeoZoneActionButtons
                                                isEditing={isEditing}
                                                onEdit={() => handleEditGeoZone(item)}
                                                onSave={() => handleSaveGeoZone(item)}
                                                onDelete={() => {
                                                    handleDelete(item);
                                                }}
                                                onCancel={handleCancelEdit}
                                            />
                                        }
                                        align="center"
                                    />
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </TableContainer>
    );
};

export default BaseTableGeoZonesDetailPTZ;
